import { useCallback } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { PayrollIssuesTableIssueFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import {
  ADDITIONAL_COMPENSATION_ISSUE,
  INVALID_BANK_ACCOUNT_NUMBER,
  NO_BANK_ACCOUNT_NUMBER,
} from 'modules/payroll_bundle/lib/constants'
import { getPayrollIssueUrl } from 'modules/payroll_bundle/lib/payrollIssueNavigation'

import { usePayrollIssuesDataCollectionSource } from './usePayrollIssuesDataCollectionSource'

type Issue = PayrollIssuesTableIssueFragment

function showPersonColumn(issue: Issue): boolean {
  return [
    INVALID_BANK_ACCOUNT_NUMBER,
    NO_BANK_ACCOUNT_NUMBER,
    ADDITIONAL_COMPENSATION_ISSUE,
  ].includes(issue.issueType)
}

interface Props {
  policyPeriodId: string
}

const PayrollIssuesTable = ({ policyPeriodId }: Props) => {
  const { navigation } = useNavigation()

  const getItemUrl = useCallback(
    (issue: Issue) => getPayrollIssueUrl(issue, navigation),
    [navigation]
  )

  const source = usePayrollIssuesDataCollectionSource({
    policyPeriodId,
    getItemUrl,
  })

  const visualizations = [
    {
      type: 'table' as const,
      options: {
        columns: [
          {
            label: i18n.t('payroll_bundle.payroll_issues.columns.employee'),
            render: (issue: Issue) => {
              if (issue.employee && showPersonColumn(issue)) {
                const [firstName, ...lastParts] = (issue.employee.fullName ?? '').split(' ')
                return {
                  type: 'person' as const,
                  value: {
                    firstName: firstName ?? '',
                    lastName: lastParts.join(' ') ?? '',
                    avatar: issue.employee.access.avatar,
                  },
                }
              }
              return { type: 'text' as const, value: '—' }
            },
          },
          {
            label: i18n.t('payroll_bundle.payroll_issues.columns.issue'),
            render: (issue: Issue) => ({
              type: 'text' as const,
              value: issue.title,
            }),
          },
          {
            label: i18n.t('payroll_bundle.payroll_issues.columns.description'),
            render: (issue: Issue) => ({
              type: 'text' as const,
              value: issue.description,
            }),
          },
        ],
      },
    },
  ]

  return <OneDataCollection source={source} visualizations={visualizations} />
}

export default observer(PayrollIssuesTable)
